// Canonical listing taxonomy. The manual seller form's CATEGORY_OPTIONS /
// SUBTYPE_OPTIONS are the source of truth; the AI Quick Add vision flow emits a
// richer, looser vocabulary (e.g. "herbs", "baked_goods", "vegetables"). Every
// write path runs through normalizeListingClassification so both paths store the
// same canonical category + subtype, and an AI listing's category/subtype
// pre-fill correctly on edit instead of showing blank.

// AI / loose value -> canonical { category, subtype? }. Keys are lowercased.
// Canonical categories: produce, eggs, baked, prepared, meat, pantry, flowers,
// homemade, garden, other. Subtypes are valid values from the form's
// SUBTYPE_OPTIONS for the mapped category.
const CLASSIFICATION: Record<string, { category: string; subtype?: string }> = {
  // produce
  produce: { category: "produce" },
  vegetables: { category: "produce", subtype: "vegetables" },
  vegetable: { category: "produce", subtype: "vegetables" },
  fruits: { category: "produce", subtype: "fruit" },
  fruit: { category: "produce", subtype: "fruit" },
  herbs: { category: "produce", subtype: "herbs" },
  herb: { category: "produce", subtype: "herbs" },
  greens: { category: "produce", subtype: "greens" },
  mushrooms: { category: "produce", subtype: "mushrooms" },
  roots: { category: "produce", subtype: "roots" },
  tomatoes: { category: "produce", subtype: "tomatoes" },
  // eggs & dairy
  eggs: { category: "eggs", subtype: "eggs" },
  egg: { category: "eggs", subtype: "eggs" },
  dairy: { category: "eggs" },
  milk: { category: "eggs", subtype: "milk" },
  butter: { category: "eggs", subtype: "butter" },
  cheese: { category: "eggs", subtype: "cheese" },
  yogurt: { category: "eggs", subtype: "yogurt" },
  // baked
  baked: { category: "baked" },
  baked_goods: { category: "baked" },
  bread: { category: "baked", subtype: "bread" },
  cookies: { category: "baked", subtype: "cookies" },
  pastry: { category: "baked", subtype: "pastry" },
  cake: { category: "baked", subtype: "cake" },
  // prepared
  prepared: { category: "prepared" },
  prepared_foods: { category: "prepared" },
  "hot sauce": { category: "prepared", subtype: "hot-sauce" },
  "hot-sauce": { category: "prepared", subtype: "hot-sauce" },
  hotsauce: { category: "prepared", subtype: "hot-sauce" },
  "chili sauce": { category: "prepared", subtype: "hot-sauce" },
  // meat & poultry
  meat: { category: "meat" },
  poultry: { category: "meat", subtype: "poultry" },
  pork: { category: "meat", subtype: "pork" },
  beef: { category: "meat", subtype: "beef" },
  lamb: { category: "meat", subtype: "lamb" },
  goat: { category: "meat", subtype: "other" },
  chicken: { category: "meat", subtype: "poultry" },
  sausage: { category: "meat", subtype: "other" },
  bacon: { category: "meat", subtype: "pork" },
  "whole animal": { category: "meat", subtype: "share" },
  "animal share": { category: "meat", subtype: "share" },
  hog: { category: "meat", subtype: "share" },
  // pantry
  pantry: { category: "pantry" },
  preserves: { category: "pantry", subtype: "jam" },
  jam: { category: "pantry", subtype: "jam" },
  honey: { category: "pantry", subtype: "honey" },
  // flowers
  flowers: { category: "flowers" },
  flower: { category: "flowers" },
  // homemade / crafts
  homemade: { category: "homemade" },
  crafts: { category: "homemade" },
  craft: { category: "homemade" },
  candles: { category: "homemade", subtype: "candle" },
  candle: { category: "homemade", subtype: "candle" },
  soap: { category: "homemade", subtype: "soap" },
  // garden / plants
  garden: { category: "garden" },
  plants: { category: "garden", subtype: "plant" },
  plant: { category: "garden", subtype: "plant" },
  houseplant: { category: "garden", subtype: "plant" },
  houseplants: { category: "garden", subtype: "plant" },
  seedlings: { category: "garden", subtype: "seedling" },
  seedling: { category: "garden", subtype: "seedling" },
  starter: { category: "garden", subtype: "starter" },
  soil: { category: "garden", subtype: "soil" },
  // clothing / apparel (upcycled, 1-of-1). Garment type rides on subtype and is
  // preserved by the "provided subtype wins" rule below.
  clothing: { category: "clothing" },
  apparel: { category: "clothing" },
  tee: { category: "clothing", subtype: "tee" },
  tshirt: { category: "clothing", subtype: "tee" },
  "t-shirt": { category: "clothing", subtype: "tee" },
  hoodie: { category: "clothing", subtype: "hoodie" },
  sweatshirt: { category: "clothing", subtype: "hoodie" },
  crewneck: { category: "clothing", subtype: "hoodie" },
  jacket: { category: "clothing", subtype: "jacket" },
  outerwear: { category: "clothing", subtype: "jacket" },
  jeans: { category: "clothing", subtype: "pants" },
  pants: { category: "clothing", subtype: "pants" },
  bottoms: { category: "clothing", subtype: "pants" },
  // catch-alls
  mixed: { category: "other" },
  other: { category: "other" },
};

export function normalizeListingClassification(
  rawCategory: string | null | undefined,
  rawSubtype?: string | null,
): { category: string; subtype: string | null } {
  const key = String(rawCategory || "").trim().toLowerCase();
  // Unknown falls to "other", never "produce". Defaulting an unclassified item
  // into a real food category asserts something nobody checked: "produce" is in
  // SNAP_ELIGIBLE_CATEGORIES (shared/snap.ts), so a candle with no category was
  // reading as EBT-eligible. "other" is canonical, SNAP-ineligible, and not a
  // food-risk category, so an unknown stays honestly unknown.
  const mapped = CLASSIFICATION[key] || { category: "other" };
  // A subtype the seller explicitly chose (manual form) always wins; otherwise
  // derive it from the (AI) category when that category implies one.
  const provided = String(rawSubtype || "").trim();
  return { category: mapped.category, subtype: provided || mapped.subtype || null };
}
